import { getAxiosInstance } from "../../../redux/common";

const api = () => getAxiosInstance();

const get = (url, config) => api().get(url, config);
const post = (url, body, config) => api().post(url, body, config);
const put = (url, body, config) => api().put(url, body, config);
const del = (url, config) => api().delete(url, config);

const CALIBRATION_STATISTICS_CACHE_TTL_MS = 30000;
let calibrationStatisticsCache = null;
let calibrationStatisticsCachedAt = 0;
let calibrationStatisticsPromise = null;
let calibrationStatisticsGeneration = 0;

export const invalidateCalibrationStatistics = () => {
  calibrationStatisticsGeneration += 1;
  calibrationStatisticsCache = null;
  calibrationStatisticsCachedAt = 0;
  calibrationStatisticsPromise = null;
};

const invalidateStatisticsAfter = (request) =>
  request.then((response) => {
    invalidateCalibrationStatistics();
    return response;
  });

export const getAllCalibrations = async (queryParams) => {
  return get("/api/calibration/filter", {
    params: queryParams,
  });
};

export const getFliteredCalibrations = async (queryParams) => {
  return getAllCalibrations(queryParams);
};

export const removeCalibrationById = async (deviceId) =>
  invalidateStatisticsAfter(del(`/api/calibration/${deviceId}`));

export const addRawNote = (calibrationId, body) =>
  post(`/api/calibration/notes/raw/${calibrationId}`, body);

export const getRawNotes = (calibrationId) =>
  get(`/api/calibration/notes/raw/${calibrationId}`);

export const addAveragedNote = (averagedId, body) =>
  post(`/api/calibration/notes/averaged/${averagedId}`, body);

export const getAveragedNotes = (averagedId) =>
  get(`/api/calibration/notes/averaged/${averagedId}`);

export const updateNote = (noteId, body) =>
  put(`/api/calibration/notes/${noteId}`, body);
export const deleteNote = (noteId) => del(`/api/calibration/notes/${noteId}`);
export const updateRawNote = (noteId, body) => updateNote(noteId, body);
export const deleteRawNote = (noteId) => deleteNote(noteId);

export const getBaseAveragedCalibrations = async () =>
  get("/api/calibration/averaged/base");
export const getManufacturerAveragedCalibrations = async () =>
  get("/api/calibration/averaged/manufacturer");
export const getBrandAveragedCalibrations = async () =>
  get("/api/calibration/averaged/brand");
export const getModelAveragedCalibrations = async () =>
  get("/api/calibration/averaged/model");

export const getCalibrationStatistics = ({ force = false } = {}) => {
  const cacheIsFresh =
    calibrationStatisticsCache &&
    Date.now() - calibrationStatisticsCachedAt <
      CALIBRATION_STATISTICS_CACHE_TTL_MS;

  if (!force && cacheIsFresh) {
    return Promise.resolve(calibrationStatisticsCache);
  }
  if (!force && calibrationStatisticsPromise) {
    return calibrationStatisticsPromise;
  }
  if (force) {
    invalidateCalibrationStatistics();
  }

  const requestGeneration = calibrationStatisticsGeneration;
  const request = get("/api/calibration/statistics")
    .then((response) => {
      if (requestGeneration !== calibrationStatisticsGeneration) {
        return getCalibrationStatistics();
      }
      calibrationStatisticsCache = response;
      calibrationStatisticsCachedAt = Date.now();
      return response;
    })
    .finally(() => {
      if (calibrationStatisticsPromise === request) {
        calibrationStatisticsPromise = null;
      }
    });
  calibrationStatisticsPromise = request;
  return request;
};

export const prefetchCalibrationStatistics = () =>
  getCalibrationStatistics().then(() => undefined);

export const includeCheckBox = async (id) =>
  invalidateStatisticsAfter(post("/api/calibration/averaged/include", { id }));

export const excludeCheckBox = async (id) =>
  invalidateStatisticsAfter(post("/api/calibration/averaged/exclude", { id }));

export const importFileHandler = async (file, params) => {
  const formData = new FormData();
  formData.append("file", file);

  Object.entries(params || {}).forEach(([key, value]) => {
    formData.append(key, value);
  });

  return invalidateStatisticsAfter(post("/api/calibration/import", formData));
};

export const exportFileHandler = async () => get("/api/calibration/export");

export const createListening = (ownerCalibrationId, body) =>
  invalidateStatisticsAfter(
    post(`/api/calibration/listening/${ownerCalibrationId}`, body)
  );

export const getListenings = (ownerCalibrationId) =>
  get(`/api/calibration/listening/${ownerCalibrationId}`);

export const getListeningDetailed = (listeningId) =>
  get(`/api/calibration/listening/detailed/${listeningId}`);

export const getListeningComponents = (listeningId) =>
  get(`/api/calibration/listening/components/${listeningId}`);

export const updateListening = (listeningId, body) =>
  invalidateStatisticsAfter(
    put(`/api/calibration/listening/detailed/${listeningId}`, body)
  );

export const deleteListening = (listeningId) =>
  invalidateStatisticsAfter(
    del(`/api/calibration/listening/detailed/${listeningId}`)
  );


export const getBluetoothSpeakerCalibrations = async (queryParams) => {
  return get("/api/bluetooth-speaker-calibration/filter", {
    params: queryParams,
  });
};

export const removeBluetoothSpeakerCalibrationById = (calibrationId) =>
  invalidateStatisticsAfter(
    del(`/api/bluetooth-speaker-calibration/${calibrationId}`)
  );

export const addBluetoothSpeakerNote = (calibrationId, body) =>
  post(`/api/calibration/notes/bluetooth-speaker/${calibrationId}`, body);

export const getBluetoothSpeakerNotes = (calibrationId) =>
  get(`/api/calibration/notes/bluetooth-speaker/${calibrationId}`);

export const updateBluetoothSpeakerNote = (noteId, body) =>
  updateNote(noteId, body);

export const deleteBluetoothSpeakerNote = (noteId) => deleteNote(noteId);

export const createBluetoothSpeakerListening = (ownerCalibrationId, body) =>
  post(`/api/calibration/listening/bluetooth-speaker/${ownerCalibrationId}`, body);

export const getBluetoothSpeakerListenings = (ownerCalibrationId) =>
  get(`/api/calibration/listening/bluetooth-speaker/${ownerCalibrationId}`);

export const bluetoothSpeakerListeningApi = {
  createListening: createBluetoothSpeakerListening,
  getListenings: getBluetoothSpeakerListenings,
  getListeningDetailed,
  getListeningComponents,
  updateListening,
  deleteListening,
};
